import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Download, Link as LinkIcon, QrCode, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function DriverQRCode() {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQRCode();
  }, []);

  const loadQRCode = async () => {
    try {
      const response = await api.get('/driver-registration/qr-code');
      setQrData(response.data);
    } catch (error) {
      console.error('Failed to load QR code:', error);
      toast.error('Failed to load QR code');
    } finally {
      setLoading(false);
    }
  };

  const downloadQR = () => {
    if (!qrData?.qr_code) return;
    
    const link = document.createElement('a');
    link.href = qrData.qr_code;
    link.download = 'driver_registration_qr.png';
    link.click();
    toast.success('QR code downloaded');
  };

  const copyLink = () => {
    if (!qrData?.registration_url) return;
    
    navigator.clipboard.writeText(qrData.registration_url);
    toast.success('Registration link copied!');
  };

  const shareWhatsApp = () => {
    if (!qrData?.registration_url) return;
    
    const message = `Join Shizah Mobility as a Driver! Register here: ${qrData.registration_url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shizah-blue"></div>
      </div>
    );
  }

  return (
    <div data-testid="qr-code-page">
      <div className="mb-8">
        <h1 className="text-4xl font-black font-heading text-gray-900 mb-2">Driver Registration QR Code</h1>
        <p className="text-gray-600">Share this QR code with drivers to let them onboard themselves</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* QR Code Display */}
        <div className="bg-white rounded-lg border border-gray-200 p-8">
          <div className="text-center">
            <div className="mb-6">
              <QrCode size={48} className="mx-auto text-shizah-blue mb-4" />
              <h2 className="text-2xl font-bold font-heading text-gray-900">Scan to Register</h2>
              <p className="text-gray-600 mt-2">Drivers can scan this QR code to register</p>
            </div>

            {qrData?.qr_code && (
              <div className="bg-gray-50 p-6 rounded-lg inline-block">
                <img
                  src={qrData.qr_code}
                  alt="Driver Registration QR Code"
                  className="w-64 h-64 mx-auto"
                />
              </div>
            )}

            <div className="mt-6 space-y-3">
              <Button
                onClick={downloadQR}
                data-testid="download-qr-button"
                className="w-full bg-shizah-blue hover:bg-blue-700 text-white"
              >
                <Download size={20} className="mr-2" />
                Download QR Code
              </Button>
              
              <Button
                onClick={shareWhatsApp}
                variant="outline"
                className="w-full border-green-500 text-green-600 hover:bg-green-50"
              >
                <Share2 size={20} className="mr-2" />
                Share via WhatsApp
              </Button>
            </div>
          </div>
        </div>

        {/* Instructions & Link */}
        <div className="space-y-6">
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-6">
            <h3 className="text-lg font-bold font-heading text-gray-900 mb-4">Registration Link</h3>
            <div className="bg-white p-3 rounded border border-blue-200 mb-3">
              <p className="text-sm font-mono break-all text-gray-700">{qrData?.registration_url}</p>
            </div>
            <Button
              onClick={copyLink}
              variant="outline"
              data-testid="copy-link-button"
              className="w-full"
            >
              <LinkIcon size={20} className="mr-2" />
              Copy Link
            </Button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-bold font-heading text-gray-900 mb-4">How to Use</h3>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start">
                <span className="bg-shizah-blue text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
                  1
                </span>
                <span>Download the QR code image or copy the registration link</span>
              </li>
              <li className="flex items-start">
                <span className="bg-shizah-blue text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
                  2
                </span>
                <span>Share it with potential drivers via WhatsApp, email, or print it</span>
              </li>
              <li className="flex items-start">
                <span className="bg-shizah-blue text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
                  3
                </span>
                <span>Drivers will scan the QR or click the link to fill the registration form</span>
              </li>
              <li className="flex items-start">
                <span className="bg-shizah-blue text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
                  4
                </span>
                <span>New registrations will appear in the Drivers list with "Pending Approval" status</span>
              </li>
              <li className="flex items-start">
                <span className="bg-shizah-blue text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
                  5
                </span>
                <span>Review and approve driver registrations from the Drivers page</span>
              </li>
            </ol>
          </div>

          <div className="bg-green-50 rounded-lg border border-green-200 p-6">
            <h3 className="text-lg font-bold font-heading text-green-900 mb-2">
              ✓ All Fields Mandatory
            </h3>
            <p className="text-sm text-green-800">
              Drivers must fill all fields including personal details, document uploads (PAN, Aadhar, License, Selfie), bank details, and references before submitting.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
